/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * Provides the persistent Russian/English locale boundary for the IAM.Voice
 * operator console. It owns catalog lookup, safe English fallback,
 * interpolation, document metadata, and the compact language switcher.
 * Product-specific copy remains in the locale catalogs.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { I18nContext, useI18n } from './I18nContext';
import {
  LOCALE_STORAGE_KEY,
  resolveInitialLocale,
  translate,
  type Locale,
  type TranslationParams,
} from './catalog';

/** Supplies locale state and translation lookup to the operator application. */
export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(resolveInitialLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, next);
    } catch {
      // In-memory switching remains available when storage is blocked.
    }
  }, []);

  const t = useCallback(
    (key: string, params?: TranslationParams) => translate(locale, key, params),
    [locale]
  );

  useEffect(() => {
    document.documentElement.lang = locale;
    document.title = t('meta.title');
    document
      .querySelector<HTMLMetaElement>('meta[name="description"]')
      ?.setAttribute('content', t('meta.description'));
  }, [locale, t]);

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Renders an accessible compact RU/EN control for the top bar and login. */
export function LocaleSwitcher({ className = '' }: { className?: string }) {
  const { locale, setLocale, t } = useI18n();
  return (
    <div
      className={`flex items-center rounded-lg border p-0.5 ${className}`}
      style={{ borderColor: 'var(--glass-border)', background: 'var(--toggle-bg)' }}
      role="group"
      aria-label={t('locale.group')}
    >
      <button
        type="button"
        aria-pressed={locale === 'ru'}
        aria-label={locale === 'ru' ? t('locale.russian') : t('locale.switch_ru')}
        className="rounded-md px-2 py-1 text-[11px] font-medium transition-colors"
        style={
          locale === 'ru'
            ? { color: 'var(--text-primary)', background: 'var(--glass-bg-from)' }
            : { color: 'var(--text-muted)' }
        }
        onClick={() => setLocale('ru')}
      >
        RU
      </button>
      <button
        type="button"
        aria-pressed={locale === 'en'}
        aria-label={locale === 'en' ? t('locale.english') : t('locale.switch_en')}
        className="rounded-md px-2 py-1 text-[11px] font-medium transition-colors"
        style={
          locale === 'en'
            ? { color: 'var(--text-primary)', background: 'var(--glass-bg-from)' }
            : { color: 'var(--text-muted)' }
        }
        onClick={() => setLocale('en')}
      >
        EN
      </button>
    </div>
  );
}
